import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase, supabaseEnabled } from '../config/supabase';

type Claims = {
  org_id?: string;
  org_role?: 'owner' | 'admin' | 'manager' | null;
  op_role?: 'operator' | 'lead' | 'trainee' | null;
  station_id?: string | null;
  is_super_admin?: boolean;
};

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  claims: Claims;
  loading: boolean;
  kind: 'admin' | 'operator' | null;
  signInAdmin: (email: string, password: string) => Promise<void>;
  signInOperator: (token: string) => void;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function decodeClaims(token: string | null | undefined): Claims {
  if (!token) return {};
  const part = token.split('.')[1];
  if (!part) return {};
  try {
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Claims;
  } catch { return {}; }
}

const OPERATOR_TOKEN_KEY = 'packflow.operator_token';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [operatorToken, setOperatorToken] = useState<string | null>(
    () => localStorage.getItem(OPERATOR_TOKEN_KEY)
  );
  const [loading, setLoading] = useState(supabaseEnabled);

  useEffect(() => {
    if (!supabase) { setLoading(false); return; }
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => { sub.subscription.unsubscribe(); };
  }, []);

  const signInAdmin = useCallback(async (email: string, password: string) => {
    if (!supabase) throw new Error('Supabase Auth nie skonfigurowane');
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const signInOperator = useCallback((token: string) => {
    localStorage.setItem(OPERATOR_TOKEN_KEY, token);
    setOperatorToken(token);
  }, []);

  const signOut = useCallback(async () => {
    localStorage.removeItem(OPERATOR_TOKEN_KEY);
    setOperatorToken(null);
    if (supabase) await supabase.auth.signOut();
  }, []);

  const adminClaims = decodeClaims(session?.access_token);
  const opClaims = decodeClaims(operatorToken);

  const kind: 'admin' | 'operator' | null = session ? 'admin' : (operatorToken ? 'operator' : null);
  const claims = kind === 'admin' ? adminClaims : opClaims;

  return (
    <AuthContext.Provider value={{
      session,
      user: session?.user ?? null,
      claims,
      loading,
      kind,
      signInAdmin,
      signInOperator,
      signOut
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

// Helper: aktualny Bearer token (admin JWT lub operator JWT) do requestow backendowych.
export function useAuthToken(): string | null {
  const { session } = useAuth();
  const adminToken = session?.access_token || null;
  const opToken = localStorage.getItem(OPERATOR_TOKEN_KEY);
  return adminToken || opToken;
}
